import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {FileText, Scale, ShieldCheck, Target, User, Video} from "lucide-react";
import {capitalize, get} from 'lodash';
import React from "react";
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {NavLink, useLocation} from "react-router-dom";
import {hasActiveMentorship} from "business/matching";
import {openChatwoot} from "common/ChatwootHelper";
import {color, font, type} from "common/tokens";

const useStyles = makeStyles(theme => ({
  sidebar: {
    width: 280,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  greeting: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  welcome: {
    fontFamily: font.SANS,
    fontSize: 24,
    fontWeight: font.BOLD,
    color: color.TEXT,
    margin: 0,
  },
  role: {
    ...type.body,
    color: color.TEXT_MUTED,
    margin: 0,
  },
  divider: {
    height: 1,
    background: color.DIVIDER,
  },
  menu: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  item: {
    ...type.body,
    fontWeight: font.MEDIUM,
    appearance: 'none',
    background: 'none',
    border: 'none',
    textAlign: 'left',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 16px',
    borderRadius: 8,
    color: color.TEXT_MUTED,
    textDecoration: 'none',
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  current: {
    background: color.PURPLE_TINT,
    color: color.PURPLE,
    fontWeight: font.SEMIBOLD,
  },
  icon: {
    width: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
}));

const ProfileSidebar = ({firstName, role}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const {pathname} = useLocation();
  const matched = useSelector(state => hasActiveMentorship(get(state, 'auth.user.status')));

  const links = [
    {to: '/moreinfo', label: t('myProfile'), icon: <User size={16}/>},
    {to: '/moreinfo/privacyPolicy', label: t('privacyPolicy'), icon: <ShieldCheck size={16}/>},
    {to: '/moreinfo/termsOfService', label: t('termsOfService'), icon: <FileText size={16}/>},
    {to: '/moreinfo/codeofconduct', label: t('codeOfConduct'), icon: <Scale size={16}/>},
    {to: '/moreinfo/faq', label: t('faqsAndWalks'), icon: <Video size={20}/>},
  ];

  return (
    <div className={classes.sidebar}>
      <div className={classes.greeting}>
        <p className={classes.welcome}>{t('welcomeName', {name: firstName})}</p>
        {Boolean(role) && <p className={classes.role}>{capitalize(role)}</p>}
      </div>

      <div className={classes.divider}/>

      <nav className={classes.menu}>
        {links.map(link => (
          <NavLink
            key={link.to}
            to={link.to}
            className={classNames(classes.item, pathname === link.to && classes.current)}
          >
            <span className={classes.icon}>{link.icon}</span>
            {link.label}
          </NavLink>
        ))}
        {/* Support is the widget in the corner rather than a page, so this
            opens it instead of navigating. */}
        {!matched && (
          <button type={'button'} className={classes.item} onClick={openChatwoot}>
            <span className={classes.icon}><Target size={16}/></span>
            {t('activeSupport')}
          </button>
        )}
      </nav>
    </div>
  );
};

export default ProfileSidebar;
